import { feathers } from '@/feathers-client';
import { RecentSearchLimit } from '../config/search';
import { ESSearchHeader } from '@/Core/config/es-search-header';

function isContainedInRecentSearches(recentSearches, payload) {
  // collect all contact ids
  let contactIds = recentSearches.map(recentSearch => {
    return recentSearch.id;
  });

  // filter all ids that don't match payload.id
  contactIds = contactIds.filter(contactId => {
    return contactId === payload;
  });

  return contactIds.length;
}

const initialState = {
  search: null,
  error: false,
  isLoading: null,
  searchResults: [],
  recentSearches: [],
};

/*
 * The contacts search module
 */
export default {
  namespaced: true,
  state: { ...initialState },
  getters: {
    /**
     * format search results so it can be used anywhere in the app
     * that requires a dropdown or autocomplete
     */
    contactsList(state) {
      return state.searchResults.map(contact => {
        return {
          text: `${contact.first_name} ${contact.last_name}`,
          id: contact.id,
          is_hidden_from_team: contact.is_hidden_from_team,
        };
      });
    },
    searchItems(state) {
      const { searchResults, recentSearches, search } = state;
      let searchItems = [];

      if (!searchResults.length && !recentSearches.length && !search) {
        return [{ header: 'Try searching for a contact' }];
      }
      if (search) {
        if (searchResults.length) {
          searchItems = [
            { header: 'Search results' },
            { divider: true, inset: false },
            ...searchResults,
          ];
        } else {
          searchItems = [
            { header: 'No matching contacts' },
          ];
        }
      }

      if (!search) {
        if (recentSearches.length) {
          return [
            ...searchItems,
            { header: 'Recent Searches' },
            ...recentSearches,
          ];
        }
        searchItems.push({ header: 'No recent searches' });
      }
      return searchItems;
    },
  },

  mutations: {
    setContactSearchError(state, payload) {
      state.error = payload;
    },
    setSearch(state, payload) {
      state.search = payload;
    },
    setSearchResults(state, payload) {
      state.searchResults = payload;
    },
    setIsLoading(state, payload) {
      state.isLoading = payload;
    },
    removeRecentSearch(state, payload) {
      state.recentSearches = state.recentSearches.filter(recentSearch => {
        return payload.id !== recentSearch.id;
      });
    },

    /**
     * Sets the recentSearches state if the recent search being
     * added is not already contained in recentSearches,

     * @param {Array}  recentSearches the recentSearches array
     * @param {Object} payload        the contact object
     */
    setRecentSearch({ recentSearches }, payload) {
      // if contact is already in recent search, do nothing
      if (isContainedInRecentSearches(recentSearches, payload.id)) {
        return;
      }

      if (recentSearches.length < RecentSearchLimit) {
        recentSearches.push(payload);
      } else {
        recentSearches.pop();
        recentSearches.push(payload);
      }
    },
    resetSearchResults(state) {
      state.searchResults = initialState.searchResults;
      state.search = initialState.search;
    },
  },

  actions: {
    async findContacts({ commit }, query = { $limit: 50 }) {
      
      try {
        const contacts = await feathers.service('contacts').find(
          { query, headers: ESSearchHeader },
        );
        commit('setSearchResults', contacts.data);
      } catch (error) {
        commit('setContactSearchError', true);
      }
    },
    /**
     * Makes the find API request on the contacts endpoint. Does a $like
     * on the first_name, last_name and email.
     *
     * @param  {String}  [search=''] the search string
     * @return {Promise}             resolves to the API response
     */
    async searchContacts({ commit, dispatch }, search = '') {
      // TODO cc - need to work around this non-empty search param issue
      if (!!search && search.length > 1) {
        const query = {
          $or: [
            { first_name: { $like: `%${search}%` } },
            { last_name: { $like: `%${search}%` } },
            { email: { $like: `%${search}%` } },
          ],
        };

        // this block deals with the case where the user wants to search
        // for a specific first and last name, e.g 'John Doe'
        const searchArgs = search.split(' ').filter(strings => {
          return strings.length > 0;
        });

        if (searchArgs.length === 2) {
          query.$or.push(
            [
              { first_name: { $like: `%${searchArgs[0]}%` } },
              { last_name: { $like: `%${searchArgs[1]}%` } },
            ],
          );
        }

        let response;
        try {
          commit('setIsLoading', true);
          response = await feathers.service('contacts').find(
            { query, headers: ESSearchHeader },
          );
        } catch (error) {
          commit('setContactSearchError', true);
        }
        commit('setSearch', search);
        commit('setIsLoading', false);
        commit('setSearchResults', response.data);
      } else {
        commit('setSearch', null);
      }
    },
    resetSearchResults({ commit }) {
      commit('resetSearchResults');
    },
    setSearch({ commit }, search = '') {
      commit('setSearch', search);
    },
    setSearchResults({ commit }, searchResults = []) {
      commit('setSearchResults', searchResults);
    },
  },
};
